import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { initializeTelemetry, telemetry, userActions } from './services/telemetry';
import ErrorBoundary from './components/ErrorBoundary';
import { ClerkProvider } from '@clerk/clerk-react';
import ProtectedRoute from './auth/ProtectedRoute';
// Using the new Dashboard for analysis functionality instead of the old AnalysisSection
import { Dashboard } from './components/Dashboard/Dashboard';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import AttorneyApp from './attorney/AttorneyApp';
import MarketingApp from './marketing/MarketingApp';
import { TrademarkProvider, ClerkUserSync } from './context/TrademarkContext';
import { PaymentProvider } from './context/PaymentContext';
import { UserApplicationProvider } from './context/UserApplicationContext';
import { NotificationProvider } from './context/NotificationContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './components/Login/Login';
import AuthRedirect from './components/AuthRedirect';
import PaymentSuccess from './components/Payment/PaymentSuccess';
import { ClerkTokenSync } from './components/ClerkTokenSync';

import config from './auth/config';

// Direct use of Clerk Publishable Key
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

// Initialize telemetry on app load
initializeTelemetry();


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={config.appearance}
      >
        <ClerkTokenSync />
        <UserApplicationProvider>
          <TrademarkProvider>
            <PaymentProvider>
              <NotificationProvider>
                <Router>
                          <ClerkUserSync />
                          <div className="min-h-screen flex flex-col">
                            <Routes>
                              {/* Redirect root to dashboard */}
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              {/* Auth Routes */}
                              <Route path="/sign-in/*" element={<Login mode="sign-in" redirectUrl="/auth-redirect" />} />
                              <Route path="/sign-up/*" element={<Login mode="sign-up" redirectUrl="/auth-redirect" />} />
                              <Route path="/auth-redirect" element={<AuthRedirect />} />

                              {/* Dashboard is now accessible without authentication */}
                              {/* Authentication gates are implemented within the dashboard for specific steps */}
                              <Route path="/dashboard/*" element={<Dashboard />} />
                              
                              {/* Payment Success Page */}
                              <Route path="/payment/success" element={<PaymentSuccess />} />
                              
                              {/* Admin Dashboard - Protected Route */}
                              <Route
                                path="/admin"
                                element={
                                  <ProtectedRoute>
                                    <AdminDashboard />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Attorney Dashboard - Protected Route */}
                              <Route
                                path="/attorney/*"
                                element={
                                  <ProtectedRoute>
                                    <AttorneyApp />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Marketing Ops Dashboard - Protected + Admin Only */}
                              <Route
                                path="/marketing/*"
                                element={
                                  <ProtectedRoute>
                                    <MarketingApp />
                                  </ProtectedRoute>
                                }
                              />
                              {/* Catch-all to dashboard */}
                              <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                          </div>
                </Router>
              </NotificationProvider>
            </PaymentProvider>
          </TrademarkProvider>
        </UserApplicationProvider>
      </ClerkProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
